/**
 * Audit Store
 * 
 * Tracks all significant events in the prioritization system:
 * - State transitions
 * - Value proposals and acceptances
 * - Lock/unlock events
 * - Session events
 * 
 * Provides an audit trail for compliance and debugging.
 * 
 * SUPABASE-ONLY PERSISTENCE:
 * - Primary: Supabase (single source of truth)
 * - No localStorage cache (eliminates stale data issues)
 */

import { create } from 'zustand';
import type { ItemDetailedState } from '../../types/prioritization/itemLifecycle';
import { formatAltitude } from '../../lib/altitudeDisplay';
import type { SessionState } from '../../types/prioritization/session';
import {
  fetchAll,
  insertRecord,
  isSupabaseAvailable,
  createInitialSyncState,
  generateUUID,
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
  type SyncState,
} from '../../lib/supabaseSync';

const GOVERNANCE_TABLE = 'governance_events';
const AUDIT_TABLE = 'audit_events';

/** Event types that are governance-only (major decisions/actions). All others with itemId go to audit_events (item trail). */
const GOVERNANCE_EVENT_TYPES = new Set([
  'session_created',
  'cycle_published',
  'ItemPoolLocked',
  'ItemPoolUnlocked',
  'ItemPoolCreated',
  'PublishCompleted',
  'PublishAttempted',
  'FocusListArchived',
  'session_state_changed',
  // Stage 6 — authority gap audit
  'official_result_set',
  'session_published',
  'workflow_config_changed',
  'workspace_default_workflow_changed',
  'workflow_created',
  'workflow_archived',
]);

const STAGE6_GOVERNANCE_TYPES = [
  'official_result_set',
  'session_published',
  'workflow_config_changed',
  'workspace_default_workflow_changed',
  'workflow_created',
  'workflow_archived',
] as const;

/** Build governance_events row for Stage6 (and other governance) events. Returns null if not a persistable governance event. */
function buildGovernanceDbRecord(
  eventData: Omit<Stage6GovernanceEvent, 'id' | 'timestamp'> | (Omit<AuditEvent, 'id' | 'timestamp'> & { type: string }),
  id: string,
  now: Date
): Record<string, unknown> | null {
  const eventType = String(eventData.type || '');
  if (!GOVERNANCE_EVENT_TYPES.has(eventType) || eventType === 'session_state_changed') return null;
  const sessionId = ('sessionId' in eventData ? eventData.sessionId : null) ?? null;
  const cycleId = ('cycleId' in eventData ? eventData.cycleId : null) ?? (('details' in eventData && eventData.details && typeof eventData.details === 'object' && 'cycleId' in eventData.details) ? (eventData.details as { cycleId?: string }).cycleId : null) ?? null;
  const loopId = ('loopId' in eventData ? eventData.loopId : null) ?? null;
  const prioritySetId = ('prioritySetId' in eventData ? eventData.prioritySetId : null) ?? null;
  const isStage6 = STAGE6_GOVERNANCE_TYPES.includes(eventType as (typeof STAGE6_GOVERNANCE_TYPES)[number]);
  const workflowId = ('workflowId' in eventData ? eventData.workflowId : null) ?? null;
  const workspaceId = ('workspaceId' in eventData ? eventData.workspaceId : null) ?? null;
  const trackId = ('trackId' in eventData ? eventData.trackId : null) ?? null;
  const hasParent = !!(sessionId || cycleId || loopId || prioritySetId || (isStage6 && (workflowId || workspaceId || trackId)));
  if (!hasParent) return null;
  // DB constraint governance_events_has_parent requires at least one parent FK on the row.
  // Stage6 workflow/workspace events only have workspace_id (no session/cycle/loop/priority_set); skip insert when we cannot set any.
  const canSatisfyConstraint = !!(sessionId || cycleId || loopId || prioritySetId || (isStage6 && workspaceId));
  if (!canSatisfyConstraint) return null;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const rawActorId = eventData.actorId ?? null;
  const actorId = rawActorId && uuidRegex.test(rawActorId) ? rawActorId : null;
  const stage6Details = isStage6 ? { ...('details' in eventData ? eventData.details : null), workflowId, workspaceId, trackId } : null;
  const itemIdForDb = ('itemId' in eventData && typeof eventData.itemId === 'string') ? eventData.itemId : null;
  // Default workspace_id to the current workspace when the event didn't carry one explicitly.
  // Fix (mig 341 companion): session_created and other governance events were landing with
  // workspace_id = NULL because the event payload types (SessionEvent, etc.) don't include a
  // workspaceId field. Falling back to getCurrentWorkspaceId() closes that gap without changing
  // any event type signature.
  const resolvedWorkspaceId = workspaceId ?? getCurrentWorkspaceId() ?? null;
  const row: Record<string, unknown> = {
    id,
    event_type: eventType,
    occurred_at: now.toISOString(),
    actor_id: actorId,
    actor_name: eventData.actorName || null,
    note: ('reason' in eventData ? eventData.reason : null) || ('note' in eventData ? eventData.note : null) || ('rationale' in eventData ? eventData.rationale : null) || null,
    rationale: ('rationale' in eventData ? eventData.rationale : null) || null,
    from_state: ('fromState' in eventData ? eventData.fromState : null) || null,
    to_state: ('toState' in eventData ? eventData.toState : null) || null,
    session_id: sessionId,
    cycle_id: cycleId,
    loop_id: loopId,
    priority_set_id: prioritySetId,
    item_id: itemIdForDb,
    before_state_snapshot: ('metadata' in eventData ? eventData.metadata : null) || null,
    after_state_snapshot: stage6Details ?? (('details' in eventData ? eventData.details : null) || null),
  };
  if (resolvedWorkspaceId != null) {
    row.workspace_id = resolvedWorkspaceId;
  }
  return row;
}

// Field edits keep their change in `payload.details` ({ field, from, to }), which
// only History read, so the Timeline and Journey showed a bare "Classification".
// These are the fields those two screens show; ids (parent, duplicate-of) are
// left out because a raw id says nothing to a reader.
const FIELD_NOTE_LABELS: Record<string, string> = {
  itemCategory: 'Category',
  altitudeLevel: 'Altitude',
  dedupeStatus: 'Dedupe status',
  work_type: 'Work type',
};

/** "Category: Problem → Opportunity" for a field-edit payload, or undefined when it holds no such change. */
export function fieldChangeNote(payload?: Record<string, unknown> | null): string | undefined {
  if (!payload) return undefined;
  // Governance rows keep the details at the top level (after_state_snapshot).
  const d = payload.details && typeof payload.details === 'object' ? (payload.details as Record<string, unknown>) : payload;
  const label = typeof d.field === 'string' ? FIELD_NOTE_LABELS[d.field] : undefined;
  if (!label || (d.from === undefined && d.to === undefined)) return undefined;
  const show = (v: unknown): string => {
    if (v === undefined || v === null || v === '') return '—';
    if (d.field === 'altitudeLevel' && typeof v === 'number') {
      return formatAltitude(v, { category: typeof d.itemCategory === 'string' ? d.itemCategory : undefined, short: true }) ?? String(v);
    }
    const s = String(v).replace(/_/g, ' ').toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
  };
  return `${label}: ${show(d.from)} → ${show(d.to)}`;
}

// ============================================================================
// Audit Event Types
// ============================================================================

/**
 * Base audit event
 */
export interface BaseAuditEvent {
  id: string;
  timestamp: Date;
  actorId: string;
  actorRole: string;
  actorName?: string;
}

/**
 * Item state transition event
 */
export interface ItemTransitionEvent extends BaseAuditEvent {
  type: 'item_transition';
  itemId: string;
  itemTitle?: string;
  fromState: ItemDetailedState;
  toState: ItemDetailedState;
  action: string;
  reason?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Value proposal event
 */
export interface ValueProposalEvent extends BaseAuditEvent {
  type: 'value_proposal';
  itemId: string;
  attributeKey: string;
  proposedValue: unknown;
  proposerRole: string;
  rationale?: string;
  confidence?: number;
}

/**
 * Value acceptance event
 */
export interface ValueAcceptanceEvent extends BaseAuditEvent {
  type: 'value_acceptance';
  itemId: string;
  attributeKey: string;
  acceptedValue: unknown;
  proposalId?: string;
  acceptanceMethod: 'consensus' | 'vote' | 'authority' | 'facilitator_override';
  sessionId?: string;
  note?: string;
}

/**
 * Value rejection event
 */
export interface ValueRejectionEvent extends BaseAuditEvent {
  type: 'value_rejection';
  itemId: string;
  attributeKey: string;
  rejectedValue: unknown;
  proposalId: string;
  reason: string;
}

/**
 * Exception acceptance event
 */
export interface ExceptionAcceptanceEvent extends BaseAuditEvent {
  type: 'exception_acceptance';
  itemId: string;
  attributeKey: string;
  reason: string;
  scope: 'global' | 'session';
  sessionId?: string;
}

/**
 * Lock event
 */
export interface LockEvent extends BaseAuditEvent {
  type: 'lock' | 'unlock';
  itemId: string;
  lockedFields?: string[];
  /** Attribute key for value lock/unlock (stored in payload). */
  attributeKey?: string;
  reason?: string;
  approverId?: string;
}

/**
 * Session event
 */
export interface SessionEvent extends BaseAuditEvent {
  type: 'session_created' | 'session_state_changed' | 'session_criteria_updated' | 
        'session_candidate_added' | 'session_candidate_removed' | 'session_tool_run' |
        'session_override' | 'session_published';
  sessionId: string;
  sessionName?: string;
  fromState?: SessionState;
  toState?: SessionState;
  details?: Record<string, unknown>;
}

/**
 * Shaping event
 */
export interface ShapingEvent extends BaseAuditEvent {
  type: 'shaping_started' | 'shaping_completed' | 'altitude_set' | 'dedupe_resolved' |
        'attribute_schema_attached' | 'merged' | 'marked_duplicate' |
        'item_title_updated' | 'item_description_updated' | 'item_category_updated' |
        'item_altitude_updated' | 'item_dedupe_updated' | 'item_parent_updated' |
        'item_marked_complete' | 'item_deleted' | 'item_split' |
        'item_tags_updated' | 'item_work_type_updated' | 'item_launch_type_updated' |
        'item_investment_type_updated' | 'item_field_updated';
  itemId: string;
  details?: Record<string, unknown>;
}

/**
 * Provenance event recorded when an item is created (or reused) from an
 * external capture such as a whiteboard photo. Carries a human note plus the
 * structured origin so the item's Activity timeline can show where it came
 * from on the board.
 */
export interface ItemImportProvenanceEvent extends BaseAuditEvent {
  type: 'item_imported';
  itemId: string;
  /** e.g. 'whiteboard_photo'. */
  source: string;
  note?: string;
  details?: Record<string, unknown>;
}

// ============================================================================
// Core B: Governance Events
// ============================================================================

/**
 * Role assignment event
 */
export interface RoleAssignmentEvent extends BaseAuditEvent {
  type: 'role_assigned' | 'role_revoked';
  subjectUserId: string;
  subjectUserName?: string;
  role: string;
  scopeKind: string;
  scopeId: string;
  scopeName?: string;
  reason?: string;
}

/**
 * Decision right event
 */
export interface DecisionRightEvent extends BaseAuditEvent {
  type: 'decision_right_granted' | 'decision_right_revoked' | 'decision_right_invoked';
  decisionType: string;
  grantedToRoles?: string[];
  scopeKind: string;
  scopeId: string;
  relatedEntityId?: string;
  relatedEntityType?: 'session' | 'cycle' | 'item' | 'list';
  outcome?: string;
}

/**
 * Permission check event (for tracking access denials)
 */
export interface PermissionCheckEvent extends BaseAuditEvent {
  type: 'permission_denied';
  action: string;
  scopeKind: string;
  scopeId: string;
  reason: string;
  requestedResource?: string;
}

/**
 * Policy decision event (for tracking policy-based authorization)
 */
export interface PolicyDecisionEvent extends BaseAuditEvent {
  type: 'policy_decision';
  action: string;
  resourceType: string;
  resourceId: string;
  decision: 'allow' | 'deny';
  ruleId: string;
  reasonCodes: string[];
  capabilityPassed?: boolean;
  metadata?: {
    reasons?: Array<{ code: string; data?: Record<string, unknown> }>;
    [key: string]: unknown;
  };
}

// ============================================================================
// Core B: Criteria Events
// ============================================================================

/**
 * Criteria selection event
 */
export interface CriteriaSelectionEvent extends BaseAuditEvent {
  type: 'criteria_selected' | 'criteria_deselected';
  sessionId: string;
  sessionName?: string;
  criteriaIds: string[];
  criteriaNames?: string[];
  previousCriteriaIds?: string[];
}

/**
 * Criteria weighting event
 */
export interface CriteriaWeightingEvent extends BaseAuditEvent {
  type: 'criteria_weighted';
  sessionId: string;
  sessionName?: string;
  weights: Record<string, number>;
  previousWeights?: Record<string, number>;
  rationale?: string;
}

/**
 * Attribute ownership event
 */
export interface AttributeOwnershipEvent extends BaseAuditEvent {
  type: 'attribute_owner_assigned' | 'attribute_owner_removed';
  attributeKey: string;
  attributeLabel?: string;
  proposerRoles?: string[];
  approverRoles?: string[];
  scopeKind: string;
  scopeId: string;
}

// ============================================================================
// Core B: Leadership Events
// ============================================================================

/**
 * Ranking adjustment event
 */
export interface RankingAdjustmentEvent extends BaseAuditEvent {
  type: 'ranking_adjusted';
  sessionId?: string;
  cycleId?: string;
  adjustments: Array<{
    itemId: string;
    itemTitle?: string;
    originalRank: number;
    newRank: number;
    rationale: string;
  }>;
  overallRationale?: string;
}

/**
 * Cutline change event
 */
export interface CutlineChangeEvent extends BaseAuditEvent {
  type: 'cutline_changed';
  sessionId?: string;
  focusListId?: string;
  previousPosition?: number;
  newPosition: number;
  rationale?: string;
  itemsAboveLine: number;
  itemsBelowLine: number;
}

/**
 * Aggregation event
 */
export interface AggregationEvent extends BaseAuditEvent {
  type: 'aggregation_run' | 'aggregation_config_changed';
  cycleId: string;
  cycleName?: string;
  sourceSessionIds?: string[];
  method?: string;
  itemCount?: number;
  configChanges?: Record<string, { from: unknown; to: unknown }>;
}

/**
 * Cycle publication event
 */
export interface CyclePublishEvent extends BaseAuditEvent {
  type: 'cycle_published';
  cycleId: string;
  cycleName?: string;
  publishedSnapshotId?: string;
  focusListId?: string;
  itemCount: number;
  cutLinePosition?: number;
}

/**
 * Stage 6 — Authority gap audit events (official result, workflow config, default, create, archive)
 */
export interface Stage6GovernanceEvent extends BaseAuditEvent {
  type: 'official_result_set' | 'session_published' | 'workflow_config_changed' | 'workspace_default_workflow_changed' | 'workflow_created' | 'workflow_archived';
  trackId?: string;
  sessionId?: string;
  workflowId?: string;
  workspaceId?: string;
  details?: Record<string, unknown>;
}

/**
 * Union of all audit event types
 */
export type AuditEvent = 
  | ItemTransitionEvent
  | ValueProposalEvent
  | ValueAcceptanceEvent
  | ValueRejectionEvent
  | ExceptionAcceptanceEvent
  | LockEvent
  | SessionEvent
  | ShapingEvent
  | ItemImportProvenanceEvent
  // Core B: Governance events
  | RoleAssignmentEvent
  | DecisionRightEvent
  | PermissionCheckEvent
  // Policy-based authorization events
  | PolicyDecisionEvent
  // Core B: Criteria events
  | CriteriaSelectionEvent
  | CriteriaWeightingEvent
  | AttributeOwnershipEvent
  // Core B: Leadership events
  | RankingAdjustmentEvent
  | CutlineChangeEvent
  | AggregationEvent
  | CyclePublishEvent
  | Stage6GovernanceEvent;

/**
 * Display DTO for item history / audit trail.
 * Used by HistoryTab. Can be built from AuditEvent or from RPC get_item_audit_trail.
 */
export interface AuditEntry {
  id: string;
  eventType: string;
  timestamp: Date | string;
  userId?: string;
  actorName?: string;
  note?: string;
  /**
   * What a field edit changed, from its recorded details —
   * "Category: Problem → Opportunity". Kept apart from `note` (what a person
   * wrote): History prints its own line for a field edit, and would print this
   * one under it a second time.
   */
  changeSummary?: string;
  fromState?: string;
  toState?: string;
  payload?: Record<string, unknown>;
  /** Legacy: structured changes for CREATE/STATE_CHANGE/PROMOTE/DEMOTE/UPDATE */
  changes?: Record<string, { from?: unknown; to?: unknown }>;
}

// ============================================================================
// Audit Store State
// ============================================================================

interface AuditState {
  events: AuditEvent[];
  
  // Sync state
  syncState: SyncState;
  
  // Supabase sync actions
  fetchFromSupabase: () => Promise<void>;
  syncEventToSupabase: (event: AuditEvent) => Promise<{ success: boolean; error?: string }>;
  
  // Item audit trail (RPC + local)
  fetchItemAuditTrail: (itemId: string) => Promise<AuditEntry[]>;
  getEntriesForItem: (itemId: string) => AuditEntry[];
  
  // Actions - Core
  logEvent: (event: Omit<AuditEvent, 'id' | 'timestamp'>) => string;
  logItemTransition: (event: Omit<ItemTransitionEvent, 'id' | 'timestamp' | 'type'>) => string;
  logValueProposal: (event: Omit<ValueProposalEvent, 'id' | 'timestamp' | 'type'>) => string;
  logValueAcceptance: (event: Omit<ValueAcceptanceEvent, 'id' | 'timestamp' | 'type'>) => string;
  logValueRejection: (event: Omit<ValueRejectionEvent, 'id' | 'timestamp' | 'type'>) => string;
  logExceptionAcceptance: (event: Omit<ExceptionAcceptanceEvent, 'id' | 'timestamp' | 'type'>) => string;
  logLock: (event: Omit<LockEvent, 'id' | 'timestamp'>) => string;
  logSessionEvent: (event: Omit<SessionEvent, 'id' | 'timestamp'>) => string;
  logShapingEvent: (event: Omit<ShapingEvent, 'id' | 'timestamp'>) => string;
  logItemImport: (event: Omit<ItemImportProvenanceEvent, 'id' | 'timestamp'>) => string;
  
  // Actions - Core B: Governance
  logRoleAssignment: (event: Omit<RoleAssignmentEvent, 'id' | 'timestamp'>) => string;
  logDecisionRight: (event: Omit<DecisionRightEvent, 'id' | 'timestamp'>) => string;
  logPermissionDenied: (event: Omit<PermissionCheckEvent, 'id' | 'timestamp' | 'type'>) => string;
  
  // Actions - Policy authorization
  logPolicyDecision: (event: Omit<PolicyDecisionEvent, 'id' | 'timestamp' | 'type'>) => string;
  
  // Actions - Core B: Criteria
  logCriteriaSelection: (event: Omit<CriteriaSelectionEvent, 'id' | 'timestamp'>) => string;
  logCriteriaWeighting: (event: Omit<CriteriaWeightingEvent, 'id' | 'timestamp' | 'type'>) => string;
  logAttributeOwnership: (event: Omit<AttributeOwnershipEvent, 'id' | 'timestamp'>) => string;
  
  // Actions - Core B: Leadership
  logRankingAdjustment: (event: Omit<RankingAdjustmentEvent, 'id' | 'timestamp' | 'type'>) => string;
  logCutlineChange: (event: Omit<CutlineChangeEvent, 'id' | 'timestamp' | 'type'>) => string;
  logAggregation: (event: Omit<AggregationEvent, 'id' | 'timestamp'>) => string;
  logCyclePublish: (event: Omit<CyclePublishEvent, 'id' | 'timestamp' | 'type'>) => string;
  logStage6Governance: (event: Omit<Stage6GovernanceEvent, 'id' | 'timestamp'>) => string;
  /** Persist Stage6 governance event to DB and return whether it was saved. Use for high-impact actions. */
  logStage6GovernanceDurable: (event: Omit<Stage6GovernanceEvent, 'id' | 'timestamp'>) => Promise<{ id: string; auditSaved: boolean; error?: string }>;
  /** Persist a governance event (e.g. session_created) to DB and return whether it was saved. Use when audit durability matters. */
  logGovernanceEventDurable: (event: Omit<AuditEvent, 'id' | 'timestamp'>) => Promise<{ id: string; auditSaved: boolean; error?: string }>;
  
  // Queries
  getEventsForItem: (itemId: string) => AuditEvent[];
  getEventsForSession: (sessionId: string) => AuditEvent[];
  getEventsForCycle: (cycleId: string) => AuditEvent[];
  getEventsByType: (type: AuditEvent['type']) => AuditEvent[];
  getEventsByActor: (actorId: string) => AuditEvent[];
  getEventsByDateRange: (start: Date, end: Date) => AuditEvent[];
  getRecentEvents: (limit?: number) => AuditEvent[];
  getGovernanceEvents: () => AuditEvent[];
  getPolicyDecisionEvents: (options?: { action?: string; resourceId?: string; decision?: 'allow' | 'deny' }) => PolicyDecisionEvent[];
  getCriteriaEvents: (sessionId?: string) => AuditEvent[];
  getLeadershipEvents: (cycleId?: string) => AuditEvent[];
  
  // Management
  clearEvents: () => void;
  exportEvents: () => string;
}

// ============================================================================
// Helper Functions
// ============================================================================

// ============================================================================
// Audit Store
// ============================================================================

export const useAuditStore = create<AuditState>()(
  (set, get) => ({
    events: [],
    syncState: createInitialSyncState(),
      
      // ================================
      // Supabase Sync
      // ================================
      
      fetchFromSupabase: async () => {
        if (!isSupabaseAvailable()) {
          console.log('[AuditStore] Supabase not available');
          return;
        }
        
        set({ syncState: { ...get().syncState, isLoading: true, syncError: null } });
        
        const result = await fetchAll<AuditEvent>(GOVERNANCE_TABLE, { column: 'occurred_at', ascending: false }, { filterByWorkspace: true, strictWorkspaceFilter: true });
        
        if (result.error) {
          set({ 
            syncState: { 
              ...get().syncState, 
              isLoading: false, 
              syncError: result.error 
            } 
          });
          console.error('[AuditStore] Failed to fetch from Supabase:', result.error);
          return;
        }
        
        if (result.data) {
          set({ 
            events: result.data,
            syncState: { 
              ...get().syncState, 
              isLoading: false, 
              lastSyncAt: new Date(),
              syncError: null 
            }
          });
          console.log(`[AuditStore] Loaded ${result.data.length} events from Supabase`);
        }
      },
      
      syncEventToSupabase: async (event: AuditEvent) => {
        if (!isSupabaseAvailable()) {
          return { success: true };
        }
        
        const result = await insertRecord<AuditEvent>(GOVERNANCE_TABLE, event);
        
        if (result.error) {
          console.error('[AuditStore] Failed to sync event to Supabase:', result.error);
          return { success: false, error: result.error };
        }
        
        return { success: true };
      },
      
      // ================================
      // Generic Event Logging
      // ================================
      
      logEvent: (eventData) => {
        const id = generateUUID();
        const now = new Date();
        const event: AuditEvent = {
          ...eventData,
          id,
          timestamp: now,
        } as AuditEvent;
        
        // Update local state
        set((state) => ({
          events: [...state.events, event],
        }));

        const itemIdForDbEarly = ('itemId' in eventData && typeof eventData.itemId === 'string') ? eventData.itemId : null;
        // Some rows (governance loaded from Supabase) carry `eventType` instead of `type`.
        const ed = eventData as { type?: string; eventType?: string };

        // Notify watchers in-app (dynamic import to avoid circular deps and CJS require in browser)
        if (itemIdForDbEarly && !GOVERNANCE_EVENT_TYPES.has(String(ed.type || ed.eventType || ''))) {
          Promise.all([
            import('./watchesStore'),
            import('./uiStore'),
            import('./itemsStore'),
          ]).then(([{ useWatchesStore }, { useUIStore }, { useItemsStore }]) => {
            const isWatching = useWatchesStore.getState().isWatching(itemIdForDbEarly);
            if (isWatching) {
              const itemTitle = useItemsStore.getState().getItemById?.(itemIdForDbEarly)?.title ?? 'An item you are watching';
              const rawEventType = String(ed.type || ed.eventType || 'updated');
              const label = rawEventType.replace(/_/g, ' ');
              useUIStore.getState().addNotification({
                type: 'info',
                title: `Watched item updated`,
                message: `${itemTitle} — ${label}`,
                duration: 6000,
              });
            }
          }).catch(() => { /* notification is best-effort */ });
        }

        if (!isSupabaseAvailable()) return id;
        const supabase = getSafeSupabaseClient();
        if (!supabase) return id;

        const eventType = String(eventData.type || '');
        const itemIdForDb = ('itemId' in eventData && typeof eventData.itemId === 'string') ? eventData.itemId : null;
        const isGovernanceOnly = GOVERNANCE_EVENT_TYPES.has(eventType);
        const sessionId = ('sessionId' in eventData ? eventData.sessionId : null) || null;
        const cycleId = ('cycleId' in eventData ? eventData.cycleId : null) || null;
        const loopId = ('loopId' in eventData ? eventData.loopId : null) || null;
        const prioritySetId = ('prioritySetId' in eventData ? eventData.prioritySetId : null) || null;
        const stage6Types = ['official_result_set', 'session_published', 'workflow_config_changed', 'workspace_default_workflow_changed', 'workflow_created', 'workflow_archived'];
        const isStage6Governance = stage6Types.includes(eventType);
        const workflowId = ('workflowId' in eventData ? eventData.workflowId : null) || null;
        const workspaceId = ('workspaceId' in eventData ? eventData.workspaceId : null) || null;
        const trackId = ('trackId' in eventData ? eventData.trackId : null) || null;
        const hasParent = !!(sessionId || cycleId || loopId || prioritySetId || (isStage6Governance && (workflowId || workspaceId || trackId)));

        // Item-scoped events → audit_events (full trail of what happened to this item)
        if (itemIdForDb && !isGovernanceOnly) {
          const attrKey = 'attributeKey' in eventData ? eventData.attributeKey : null;
          const acceptedVal = 'acceptedValue' in eventData ? eventData.acceptedValue : null;
          const proposedVal = 'proposedValue' in eventData ? eventData.proposedValue : null;
          const rejectedVal = 'rejectedValue' in eventData ? eventData.rejectedValue : null;
          const explicitNote = ('reason' in eventData ? eventData.reason : null) ?? ('note' in eventData ? eventData.note : null) ?? ('rationale' in eventData ? eventData.rationale : null);
          const valueDetailNote =
            typeof attrKey === 'string'
              ? acceptedVal !== undefined && acceptedVal !== null
                ? `${attrKey}: ${String(acceptedVal)}`
                : proposedVal !== undefined && proposedVal !== null
                  ? `${attrKey}: ${String(proposedVal)}`
                  : rejectedVal !== undefined && rejectedVal !== null
                    ? `${attrKey}: ${String(rejectedVal)}`
                    : explicitNote
              : explicitNote;
          const payload: Record<string, unknown> = {
            note: valueDetailNote ?? explicitNote,
            fromState: ('fromState' in eventData ? eventData.fromState : null) ?? undefined,
            toState: ('toState' in eventData ? eventData.toState : null) ?? undefined,
            attributeKey: attrKey ?? undefined,
            acceptedValue: acceptedVal ?? undefined,
            proposedValue: proposedVal ?? undefined,
            rejectedValue: rejectedVal ?? undefined,
          };
          if ('details' in eventData && eventData.details && typeof eventData.details === 'object') {
            payload.details = eventData.details;
          }
          supabase
            .from(AUDIT_TABLE)
            .insert({
              id,
              event_type: eventType,
              item_id: itemIdForDb,
              payload,
              created_by: eventData.actorId ?? null,
              created_at: now.toISOString(),
              workspace_id: getCurrentWorkspaceId() ?? null,
            })
            .then(({ error }) => {
              if (error) console.error('[AuditStore] Failed to persist item audit event:', error.message);
            });
        }

        // Governance-only events → governance_events (major decisions/actions)
        if (isGovernanceOnly && hasParent) {
          if (eventType === 'session_state_changed') return id;
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          const rawActorId = eventData.actorId || null;
          const actorId = rawActorId && uuidRegex.test(rawActorId) ? rawActorId : null;
          const stage6Details = isStage6Governance ? { ...('details' in eventData ? eventData.details : null), workflowId, workspaceId, trackId } : null;
          const dbRecord: Record<string, unknown> = {
            id,
            event_type: eventType,
            occurred_at: now.toISOString(),
            actor_id: actorId,
            actor_name: eventData.actorName || null,
            note: ('reason' in eventData ? eventData.reason : null) || ('note' in eventData ? eventData.note : null) || ('rationale' in eventData ? eventData.rationale : null) || null,
            rationale: ('rationale' in eventData ? eventData.rationale : null) || null,
            from_state: ('fromState' in eventData ? eventData.fromState : null) || null,
            to_state: ('toState' in eventData ? eventData.toState : null) || null,
            session_id: sessionId,
            cycle_id: cycleId,
            loop_id: loopId,
            priority_set_id: prioritySetId,
            item_id: itemIdForDb,
            before_state_snapshot: ('metadata' in eventData ? eventData.metadata : null) || null,
            after_state_snapshot: stage6Details ?? (('details' in eventData ? eventData.details : null) || null),
          };
          // Default workspace_id to the current workspace when the event didn't carry one
          // explicitly (fix: mig 341 companion). Keeps this path consistent with
          // buildGovernanceDbRecord.
          const resolvedWorkspaceId = workspaceId ?? getCurrentWorkspaceId() ?? null;
          if (resolvedWorkspaceId != null) {
            dbRecord.workspace_id = resolvedWorkspaceId;
          }
          supabase
            .from(GOVERNANCE_TABLE)
            .insert(dbRecord)
            .then(({ error }) => {
              if (error) console.error('[AuditStore] Failed to persist governance event:', error.message);
            });
        }

        return id;
      },
      
      // ================================
      // Specific Event Logging
      // ================================
      
      // These 5 wrappers previously hardcoded the UPPERCASE governance-event
      // vocabulary (e.g. 'ITEM_PROMOTED', matching GovernanceEventType in
      // types/prioritization/governanceEvent.ts) instead of the lowercase
      // audit_events vocabulary these functions' own declared types require
      // (ItemTransitionEvent.type: 'item_transition', etc.). That's a real
      // bug, not just a type mismatch: itemsStore.ts's own direct
      // logEvent({type: 'item_transition', ...}) call for the same concept
      // already uses the lowercase form, and AuditEventViewer.tsx's display
      // switch only has cases for the lowercase values — so every event
      // logged through these 5 wrappers was falling through to the unknown/
      // default case in the audit trail UI instead of showing its proper
      // label. Fixed to match the canonical lowercase values.
      logItemTransition: (eventData) => {
        return get().logEvent({
          ...eventData,
          type: 'item_transition',
        });
      },

      logValueProposal: (eventData) => {
        return get().logEvent({
          ...eventData,
          type: 'value_proposal',
        });
      },

      logValueAcceptance: (eventData) => {
        return get().logEvent({
          ...eventData,
          type: 'value_acceptance',
        });
      },

      logValueRejection: (eventData) => {
        return get().logEvent({
          ...eventData,
          type: 'value_rejection',
        });
      },

      logExceptionAcceptance: (eventData) => {
        return get().logEvent({
          ...eventData,
          type: 'exception_acceptance',
        });
      },
      
      logLock: (eventData) => {
        return get().logEvent(eventData);
      },
      
      logSessionEvent: (eventData) => {
        return get().logEvent(eventData);
      },
      
      logShapingEvent: (eventData) => {
        return get().logEvent(eventData);
      },

      logItemImport: (eventData) => {
        return get().logEvent(eventData);
      },

      // ================================
      // Core B: Governance Event Logging
      // ================================
      
      logRoleAssignment: (eventData) => {
        return get().logEvent(eventData);
      },
      
      logDecisionRight: (eventData) => {
        return get().logEvent(eventData);
      },
      
      logPermissionDenied: (eventData) => {
        return get().logEvent({
          ...eventData,
          type: 'permission_denied',
        });
      },
      
      // ================================
      // Policy Authorization Event Logging
      // ================================
      
      logPolicyDecision: (eventData) => {
        return get().logEvent({
          ...eventData,
          type: 'policy_decision',
        });
      },
      
      // ================================
      // Core B: Criteria Event Logging
      // ================================
      
      logCriteriaSelection: (eventData) => {
        return get().logEvent(eventData);
      },
      
      logCriteriaWeighting: (eventData) => {
        return get().logEvent({
          ...eventData,
          type: 'criteria_weighted',
        });
      },
      
      logAttributeOwnership: (eventData) => {
        return get().logEvent(eventData);
      },
      
      // ================================
      // Core B: Leadership Event Logging
      // ================================
      
      logRankingAdjustment: (eventData) => {
        return get().logEvent({
          ...eventData,
          type: 'ranking_adjusted',
        });
      },
      
      logCutlineChange: (eventData) => {
        return get().logEvent({
          ...eventData,
          type: 'cutline_changed',
        });
      },
      
      logAggregation: (eventData) => {
        return get().logEvent(eventData);
      },
      
      logCyclePublish: (eventData) => {
        return get().logEvent({
          ...eventData,
          type: 'cycle_published',
        });
      },
      
      logStage6Governance: (eventData) => {
        return get().logEvent(eventData as Omit<Stage6GovernanceEvent, 'id' | 'timestamp'>);
      },
      
      logStage6GovernanceDurable: async (eventData) => {
        const id = generateUUID();
        const now = new Date();
        const event: Stage6GovernanceEvent = {
          ...eventData,
          id,
          timestamp: now,
        } as Stage6GovernanceEvent;
        set((state) => ({ events: [...state.events, event as unknown as AuditEvent] }));
        if (!isSupabaseAvailable()) {
          return { id, auditSaved: false, error: 'Database not available' };
        }
        const supabase = getSafeSupabaseClient();
        if (!supabase) {
          return { id, auditSaved: false, error: 'Database client not available' };
        }
        const dbRecord = buildGovernanceDbRecord(eventData, id, now);
        if (!dbRecord) {
          return { id, auditSaved: false, error: 'Event not persistable to governance_events' };
        }
        const { error } = await supabase.from(GOVERNANCE_TABLE).insert(dbRecord);
        if (error) {
          console.error('[AuditStore] Failed to persist governance event:', error.message);
          return { id, auditSaved: false, error: error.message };
        }
        return { id, auditSaved: true };
      },
      
      logGovernanceEventDurable: async (eventData) => {
        const id = generateUUID();
        const now = new Date();
        const event = { ...eventData, id, timestamp: now } as AuditEvent;
        set((state) => ({ events: [...state.events, event] }));
        if (!isSupabaseAvailable()) {
          return { id, auditSaved: false, error: 'Database not available' };
        }
        const supabase = getSafeSupabaseClient();
        if (!supabase) {
          return { id, auditSaved: false, error: 'Database client not available' };
        }
        const dbRecord = buildGovernanceDbRecord(eventData as Omit<AuditEvent, 'id' | 'timestamp'> & { type: string }, id, now);
        if (!dbRecord) {
          return { id, auditSaved: false, error: 'Event not persistable to governance_events' };
        }
        const { error } = await supabase.from(GOVERNANCE_TABLE).insert(dbRecord);
        if (error) {
          console.error('[AuditStore] Failed to persist governance event:', error.message);
          return { id, auditSaved: false, error: error.message };
        }
        return { id, auditSaved: true };
      },
      
      // ================================
      // Queries
      // ================================
      
      getEventsForItem: (itemId) => {
        return get().events.filter((event) => {
          if ('itemId' in event) {
            return event.itemId === itemId;
          }
          return false;
        });
      },
      
      fetchItemAuditTrail: async (itemId) => {
        const supabase = getSafeSupabaseClient();
        if (!supabase) return get().getEntriesForItem(itemId);

        const mapRowToEntry = (row: {
          id: string;
          event_type: string;
          occurred_at: string;
          actor_id?: string | null;
          actor_name?: string | null;
          note?: string | null;
          from_state?: string | null;
          to_state?: string | null;
          payload?: Record<string, unknown> | null;
          after_state_snapshot?: Record<string, unknown> | null;
        }): AuditEntry => {
          const payload = row.payload ?? row.after_state_snapshot ?? undefined;
          let note = row.note ?? (payload && typeof payload.note === 'string' ? payload.note : undefined);
          if (!note && payload && typeof payload.attributeKey === 'string') {
            const attr = payload.attributeKey;
            if (payload.acceptedValue != null) note = `${attr}: ${String(payload.acceptedValue)}`;
            else if (payload.proposedValue != null) note = `${attr}: ${String(payload.proposedValue)}`;
            else if (payload.rejectedValue != null) note = `${attr}: ${String(payload.rejectedValue)}`;
            else note = attr;
          }
          const fromState = row.from_state ?? (payload && typeof payload.fromState === 'string' ? payload.fromState : undefined);
          const toState = row.to_state ?? (payload && typeof payload.toState === 'string' ? payload.toState : undefined);
          return {
            id: row.id,
            eventType: row.event_type,
            timestamp: row.occurred_at,
            userId: row.actor_id ?? undefined,
            actorName: row.actor_name ?? undefined,
            note,
            changeSummary: fieldChangeNote(payload),
            fromState,
            toState,
            payload,
          };
        };

        // Query all three sources in parallel and merge:
        //  1. get_item_audit_trail RPC — governance_events WHERE item_id = p_item_id
        //     (session results, value acceptance, leadership approval, etc.)
        //  2. audit_events direct query — per-item shaping events (field edits,
        //     custom fields, title/category/altitude). An early-return on the RPC
        //     alone silently drops every field change for items with governance events.
        //  3. get_item_cycle_trail RPC — cycle-level governance events for any cycle
        //     the item was in (ranking runs, pool locks, criteria changes, publishes).
        //  4. governance_events WHERE item_id — the item's own governed acts (take back a
        //     retirement, un-review, accept a category suggestion…). The RPC above reads
        //     only audit_events (mig 068), so these reached only the decision trail (2026-10-15).
        const [rpcResult, auditResult, cycleResult, itemGovResult] = await Promise.all([
          supabase.rpc('get_item_audit_trail', { p_item_id: itemId }),
          supabase
            .from(AUDIT_TABLE)
            .select('id, event_type, created_at, created_by, payload')
            .eq('item_id', itemId)
            .order('created_at', { ascending: false }),
          supabase.rpc('get_item_cycle_trail', { p_item_id: itemId }),
          supabase
            .from('governance_events')
            .select('id, event_type, occurred_at, actor_id, actor_name, note, rationale, from_state, to_state, after_state_snapshot')
            .eq('item_id', itemId)
            .order('occurred_at', { ascending: false }),
        ]);
        if (itemGovResult.error) {
          console.warn('[AuditStore] governance_events by item_id failed:', itemGovResult.error.message);
        }

        if (rpcResult.error) {
          console.warn('[AuditStore] get_item_audit_trail RPC failed:', rpcResult.error.message);
        }
        if (auditResult.error) {
          console.warn('[AuditStore] audit_events query by item_id failed:', auditResult.error.message);
        }
        if (cycleResult.error) {
          console.warn('[AuditStore] get_item_cycle_trail RPC failed:', cycleResult.error.message);
        }

        const seen = new Set<string>();
        const merged: ReturnType<typeof mapRowToEntry>[] = [];

        // Governance events from RPC
        if (!rpcResult.error && Array.isArray(rpcResult.data)) {
          for (const row of rpcResult.data as Array<{
            id: string;
            event_type: string;
            occurred_at: string;
            actor_id: string | null;
            actor_name: string | null;
            note: string | null;
            from_state: string | null;
            to_state: string | null;
            payload?: Record<string, unknown> | null;
          }>) {
            if (!seen.has(row.id)) {
              seen.add(row.id);
              merged.push(mapRowToEntry(row));
            }
          }
        }

        // The item's own governance events
        if (!itemGovResult.error && Array.isArray(itemGovResult.data)) {
          for (const row of itemGovResult.data as Array<{
            id: string;
            event_type: string;
            occurred_at: string;
            actor_id: string | null;
            actor_name: string | null;
            note: string | null;
            rationale: string | null;
            from_state: string | null;
            to_state: string | null;
            after_state_snapshot: Record<string, unknown> | null;
          }>) {
            if (!seen.has(row.id)) {
              seen.add(row.id);
              merged.push(mapRowToEntry({ ...row, note: row.note ?? row.rationale }));
            }
          }
        }

        // Shaping / field-change events from audit_events
        if (!auditResult.error && Array.isArray(auditResult.data)) {
          for (const row of auditResult.data as Array<{
            id: string;
            event_type: string;
            created_at: string;
            created_by?: string | null;
            payload?: Record<string, unknown> | null;
          }>) {
            if (!seen.has(row.id)) {
              seen.add(row.id);
              merged.push(mapRowToEntry({
                id: row.id,
                event_type: row.event_type,
                occurred_at: row.created_at,
                actor_id: row.created_by ?? null,
                actor_name: null,
                note: null,
                from_state: null,
                to_state: null,
                payload: row.payload ?? undefined,
              }));
            }
          }
        }

        // Cycle-level governance events (ranking, pool lock/publish, criteria) from
        // cycles this item was in. These carry cycle_id instead of item_id so they
        // were invisible in the item trail before this change.
        if (!cycleResult.error && Array.isArray(cycleResult.data)) {
          for (const row of cycleResult.data as Array<{
            id: string;
            event_type: string;
            occurred_at: string;
            actor_id?: string | null;
            actor_name?: string | null;
            note?: string | null;
            from_state?: string | null;
            to_state?: string | null;
            payload?: Record<string, unknown> | null;
            cycle_id?: string | null;
          }>) {
            if (!seen.has(row.id)) {
              seen.add(row.id);
              merged.push(mapRowToEntry({
                ...row,
                // Tag the payload so HistoryTab can render cycle context.
                payload: { ...(row.payload ?? {}), _cycleId: row.cycle_id ?? null, _source: 'cycle' },
              }));
            }
          }
        }

        if (merged.length > 0) {
          merged.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
          return merged;
        }

        // Last resort: derive from in-memory store events
        await get().fetchFromSupabase();
        return get().getEntriesForItem(itemId);
      },
      
      getEntriesForItem: (itemId) => {
        const events = get().getEventsForItem(itemId);
        return events.map((e): AuditEntry => {
          // Governance-events rows loaded from Supabase arrive as camelCased
          // `occurredAt` / `eventType`; in-memory logged events use `timestamp` / `type`.
          // Support both shapes so this map never produces an Invalid Date.
          const govShape = e as unknown as { occurredAt?: Date | string | null; eventType?: string };
          const tsRaw = e.timestamp ?? govShape.occurredAt ?? null;
          const tsCandidate = tsRaw instanceof Date ? tsRaw : tsRaw != null ? new Date(tsRaw) : null;
          const ts = tsCandidate && !Number.isNaN(tsCandidate.getTime()) ? tsCandidate : new Date();
          const eventType = (e as { type?: string }).type ?? govShape.eventType ?? 'unknown';
          const base = { id: e.id, eventType, timestamp: ts, userId: e.actorId, actorName: e.actorName };
          if (e.type === 'item_transition' && 'fromState' in e && 'toState' in e) {
            return { ...base, fromState: String(e.fromState), toState: String(e.toState), changes: { lifecycle_state: { from: e.fromState, to: e.toState } } };
          }
          if (e.type === 'item_imported') {
            const provEvent = e as ItemImportProvenanceEvent;
            return { ...base, note: provEvent.note, payload: { note: provEvent.note, details: provEvent.details } };
          }
          const attrKey = 'attributeKey' in e ? (e as { attributeKey?: string }).attributeKey : undefined;
          const valueNote = typeof attrKey === 'string'
            ? 'acceptedValue' in e && (e as { acceptedValue?: unknown }).acceptedValue != null
              ? `${attrKey}: ${String((e as ValueAcceptanceEvent).acceptedValue)}`
              : 'proposedValue' in e && (e as { proposedValue?: unknown }).proposedValue != null
                ? `${attrKey}: ${String((e as ValueProposalEvent).proposedValue)}`
                : 'rejectedValue' in e && (e as { rejectedValue?: unknown }).rejectedValue != null
                  ? `${attrKey}: ${String((e as ValueRejectionEvent).rejectedValue)}`
                  : attrKey
            : undefined;
          // Dual-check kept intentionally: 'PROPOSAL_SUBMITTED'/'VALUE_ACCEPTED'/
          // 'VALUE_REJECTED' are the uppercase values the logValueProposal/
          // logValueAcceptance/logValueRejection wrappers used to write
          // (bug fixed above) — rows persisted before that fix still have
          // them, so both forms must keep matching here. Cast to string
          // since AuditEvent's discriminated union only declares the
          // (correct, current) lowercase literals.
          const rawType = e.type as string;
          if ((rawType === 'PROPOSAL_SUBMITTED' || e.type === 'value_proposal') && attrKey) {
            const proposedVal = 'proposedValue' in e ? (e as ValueProposalEvent).proposedValue : undefined;
            return { ...base, note: valueNote ?? attrKey, payload: { proposedValue: proposedVal, attributeKey: attrKey } };
          }
          if ((rawType === 'VALUE_ACCEPTED' || e.type === 'value_acceptance') && attrKey) {
            const acceptedVal = 'acceptedValue' in e ? (e as ValueAcceptanceEvent).acceptedValue : undefined;
            return { ...base, note: valueNote ?? attrKey, toState: 'accepted', payload: { acceptedValue: acceptedVal, attributeKey: attrKey } };
          }
          if ((rawType === 'VALUE_REJECTED' || e.type === 'value_rejection') && attrKey) {
            const rejectedVal = 'rejectedValue' in e ? (e as ValueRejectionEvent).rejectedValue : undefined;
            return { ...base, note: valueNote ?? attrKey, toState: 'rejected', payload: { rejectedValue: rejectedVal, attributeKey: attrKey } };
          }
          return base;
        });
      },
      
      getEventsForSession: (sessionId) => {
        return get().events.filter((event) => {
          if ('sessionId' in event) {
            return event.sessionId === sessionId;
          }
          return false;
        });
      },
      
      getEventsByType: (type) => {
        return get().events.filter((event) => event.type === type);
      },
      
      getEventsByActor: (actorId) => {
        return get().events.filter((event) => event.actorId === actorId);
      },
      
      getEventsByDateRange: (start, end) => {
        return get().events.filter((event) => {
          const eventTime = new Date(event.timestamp).getTime();
          return eventTime >= start.getTime() && eventTime <= end.getTime();
        });
      },
      
      getRecentEvents: (limit = 50) => {
        const events = get().events;
        return events.slice(-limit).reverse();
      },
      
      getEventsForCycle: (cycleId) => {
        return get().events.filter((event) => {
          if ('cycleId' in event) {
            return event.cycleId === cycleId;
          }
          return false;
        });
      },
      
      getGovernanceEvents: () => {
        const governanceTypes = [
          'role_assigned', 'role_revoked', 
          'decision_right_granted', 'decision_right_revoked', 'decision_right_invoked',
          'permission_denied', 'policy_decision'
        ];
        return get().events.filter((event) => governanceTypes.includes(event.type));
      },
      
      getPolicyDecisionEvents: (options?: { action?: string; resourceId?: string; decision?: 'allow' | 'deny' }) => {
        return get().events.filter((event): event is PolicyDecisionEvent => {
          if (event.type !== 'policy_decision') return false;
          if (options?.action && event.action !== options.action) return false;
          if (options?.resourceId && event.resourceId !== options.resourceId) return false;
          if (options?.decision && event.decision !== options.decision) return false;
          return true;
        });
      },
      
      getCriteriaEvents: (sessionId) => {
        const criteriaTypes = [
          'criteria_selected', 'criteria_deselected', 'criteria_weighted',
          'attribute_owner_assigned', 'attribute_owner_removed'
        ];
        const events = get().events.filter((event) => criteriaTypes.includes(event.type));
        if (sessionId) {
          return events.filter((event) => 'sessionId' in event && event.sessionId === sessionId);
        }
        return events;
      },
      
      getLeadershipEvents: (cycleId) => {
        const leadershipTypes = [
          'ranking_adjusted', 'cutline_changed', 
          'aggregation_run', 'aggregation_config_changed',
          'cycle_published'
        ];
        const events = get().events.filter((event) => leadershipTypes.includes(event.type));
        if (cycleId) {
          return events.filter((event) => 'cycleId' in event && event.cycleId === cycleId);
        }
        return events;
      },
      
      // ================================
      // Management
      // ================================
      
      clearEvents: () => {
        set({ events: [] });
      },
      
      exportEvents: () => {
      return JSON.stringify(get().events, null, 2);
    },
  })
);

// ============================================================================
// Convenience Hooks
// ============================================================================

/**
 * Get recent audit events
 */
export const useRecentAuditEvents = (limit?: number) => 
  useAuditStore((state) => state.getRecentEvents(limit));

/**
 * Get audit events for a specific item
 */
export const useItemAuditEvents = (itemId: string) =>
  useAuditStore((state) => state.getEventsForItem(itemId));

/**
 * Get audit events for a specific session
 */
export const useSessionAuditEvents = (sessionId: string) =>
  useAuditStore((state) => state.getEventsForSession(sessionId));

// ============================================================================
// Audit Event Formatters
// ============================================================================

/**
 * Format an audit event for display
 */
export function formatAuditEvent(event: AuditEvent): string {
  const timestamp = new Date(event.timestamp).toLocaleString();
  const actor = event.actorName || event.actorId;
  
  switch (event.type) {
    case 'item_transition':
      return `[${timestamp}] ${actor} transitioned item from ${event.fromState} to ${event.toState} (${event.action})`;
    
    case 'value_proposal':
      return `[${timestamp}] ${actor} proposed value "${event.proposedValue}" for ${event.attributeKey}`;
    
    case 'value_acceptance':
      return `[${timestamp}] ${actor} accepted value "${event.acceptedValue}" for ${event.attributeKey}`;
    
    case 'value_rejection':
      return `[${timestamp}] ${actor} rejected value "${event.rejectedValue}" for ${event.attributeKey}: ${event.reason}`;
    
    case 'exception_acceptance':
      return `[${timestamp}] ${actor} accepted exception for ${event.attributeKey}: ${event.reason}`;
    
    case 'lock':
      return `[${timestamp}] ${actor} locked item`;
    
    case 'unlock':
      return `[${timestamp}] ${actor} unlocked item: ${event.reason}`;
    
    case 'session_created':
      return `[${timestamp}] ${actor} created session "${event.sessionName}"`;
    
    case 'session_state_changed':
      return `[${timestamp}] ${actor} changed session state from ${event.fromState} to ${event.toState}`;
    
    case 'session_tool_run':
      return `[${timestamp}] ${actor} ran prioritization tool in session`;
    
    case 'session_published':
      return `[${timestamp}] ${actor} published session results`;
    
    case 'shaping_started':
      return `[${timestamp}] ${actor} started item work`;
    
    case 'shaping_completed':
      return `[${timestamp}] ${actor} item ready`;
    
    case 'altitude_set':
      return `[${timestamp}] ${actor} set item altitude`;
    
    case 'dedupe_resolved':
      return `[${timestamp}] ${actor} resolved dedupe status`;
    
    case 'item_split': {
      const details = event.details as { targetItemIds?: string[]; rationale?: string } | undefined;
      const count = details?.targetItemIds?.length ?? 0;
      return `[${timestamp}] ${actor} split item into ${count} item${count !== 1 ? 's' : ''}`;
    }
    
    case 'policy_decision': {
      const decisionResult = event.decision === 'allow' ? 'allowed' : 'denied';
      return `[${timestamp}] ${actor} was ${decisionResult} for action "${event.action}" on ${event.resourceType}:${event.resourceId} (rule: ${event.ruleId})`;
    }
    
    default:
      return `[${timestamp}] ${actor} performed action: ${event.type}`;
  }
}

/**
 * Get a summary of audit events for an item
 */
export function getItemAuditSummary(events: AuditEvent[]): {
  totalEvents: number;
  transitionCount: number;
  valueProposalCount: number;
  valueAcceptanceCount: number;
  lastEvent?: AuditEvent;
  uniqueActors: string[];
} {
  const transitionCount = events.filter(e => e.type === 'item_transition').length;
  const valueProposalCount = events.filter(e => e.type === 'value_proposal').length;
  const valueAcceptanceCount = events.filter(e => e.type === 'value_acceptance').length;
  const uniqueActors = [...new Set(events.map(e => e.actorId))];
  const lastEvent = events.length > 0 ? events[events.length - 1] : undefined;
  
  return {
    totalEvents: events.length,
    transitionCount,
    valueProposalCount,
    valueAcceptanceCount,
    lastEvent,
    uniqueActors,
  };
}
